import DetailPageLayout from "@/components/layout/detailPageLayout/DetailPageLayout";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DetailsTab from "./DetailsTab";
import SvgIcon from "@/components/common/SvgIcon";
import { Button, ButtonsMenu } from "@/components/kit";
import { useGetUserById } from "@/hooks/query/admins/useGetUserById";
import RevokeUserDialog from "../adminsList/RevokeUserDialog";
import { IUserResponse } from "../types";
import useRemoveUser from "@/hooks/query/admins/useRemoveUser";
import { useQueryClient } from "@tanstack/react-query";
import useStoreMe from "@/hooks/store/useStoreMe";
import useBreadcrumbs from "@/hooks/store/useBreadcrumbs";

const MspAdminDetails = () => {
  const { t } = useTranslation(["common"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setRoutes } = useBreadcrumbs();
  const [selectedUser, setSelectedUser] = useState<IUserResponse | null>(null);
  const activeTab = searchParams.get("tab") || "details";
  const params = useParams();
  const userId = params?.id as string;
  const { data: user, isFetching } = useGetUserById(userId, {
    enabled: !!userId,
  });
  const { me } = useStoreMe();
  const { mutate: revokeUser, isPending: isRevokingUser } = useRemoveUser();
  const queryClient = useQueryClient();
  const handleRevokeUser = (id: string, callback?: () => void) => {
    revokeUser(id, {
      onSuccess: () => {
        setSelectedUser(null);
        queryClient.invalidateQueries({ queryKey: ["users"] });
        callback?.();
      },
    });
  };

  useEffect(() => {
    setRoutes([
      {
        name: "Admin center",
        path: "/admins",
      },
      {
        name: "MSP admins",
        path: "/admins",
      },
      {
        name: "Admin details",
        description:
          "View and manage an admin’s profile, access permissions, and assigned organizations. Use this page to adjust roles, monitor activity, or revoke access when necessary.",
        // helpLink:
        //   "https://help.trio.so/en/articles/12529648-it-admins-in-trio-msp",
      },
    ]);
    return () => {
      setRoutes([]);
    };
  }, []);

  const content = useMemo(() => {
    return {
      details: <DetailsTab user={user} isFetching={isFetching} />,
    };
  }, [activeTab, isFetching, user]);
  const defaultList: {
    title: string;
    icon?: string;
    value: string;
    isAccessible?: boolean | null;
    disable?: boolean;
  }[] = [
    {
      title: t("details"),
      value: "details",
    },

    // {
    //   title: t("Activity log"),
    //   value: "activity-log",
    // },
  ];
  const navigate = useNavigate();
  return (
    <DetailPageLayout
      title="Admin Details"
      description={
        <div className="flex w-full justify-between items-center">
          <span>View admin details</span>
          {user?.id !== me?.id && (
            <ButtonsMenu
              button={
                <Button variant="outlined" color="gray">
                  Actions
                  <SvgIcon
                    iconName="arrowRight"
                    className="w-4 h-4"
                    fill="fill-gray-600"
                  />
                </Button>
              }
              lists={[
                {
                  id: "delete",
                  title: "Delete",

                  onClick: () => {
                    setSelectedUser(user ?? null);
                  },
                },
              ]}
            />
          )}
        </div>
      }
      backText="Back"
      onBack={() => navigate("/admins")}
      tabs={defaultList}
      activeTab={activeTab}
      onTabChange={(v) => {
        searchParams.set("tab", v);
        setSearchParams(searchParams);
      }}
    >
      {content[activeTab as keyof typeof content]}
      <RevokeUserDialog
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        onRevokeUser={handleRevokeUser}
        isRevokingUser={isRevokingUser}
      />
    </DetailPageLayout>
  );
};

export default MspAdminDetails;
